import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import LandingNav from "./components/LandingNav";
import { supabase } from "@/lib/supabaseClient";

const heroSlides = [
  {
    image: "https://readdy.ai/api/search-image?query=elegant%20wedding%20photography%20couple%20at%20sunset%20golden%20hour%20romantic%20atmosphere%20professional%20photographer%20capturing%20moment%20luxury%20venue%20dark%20moody%20dramatic%20lighting&width=1920&height=1080&seq=slide1&orientation=landscape",
    tag: "SOFTWARE PARA FOTÓGRAFOS",
    title: "Gestiona tu negocio de fotografía",
    gold: "con Pixory AI.",
    subtitle: "Controla clientes, contratos, pagos, entregas y portales desde un solo sistema.",
  },
  {
    image: "https://readdy.ai/api/search-image?query=professional%20corporate%20headshot%20photography%20studio%20modern%20office%20business%20portrait%20dark%20background%20dramatic%20lighting%20professional%20photographer%20equipment&width=1920&height=1080&seq=slide2&orientation=landscape",
    tag: "SOFTWARE PARA FOTÓGRAFOS",
    title: "Perfecto para estudios de fotografía y video",
    gold: null,
    subtitle: "Quinceañeras, bodas, eventos, retratos, corporativo y más.",
  },
  {
    image: "https://readdy.ai/api/search-image?query=newborn%20baby%20maternity%20photography%20studio%20soft%20warm%20light%20tender%20moment%20professional%20photographer%20pastel%20tones%20elegant%20minimal%20setup&width=1920&height=1080&seq=slide3&orientation=landscape",
    tag: "SOFTWARE PARA FOTÓGRAFOS",
    title: "Convierte más clientes y automatiza tu flujo",
    gold: null,
    subtitle: "Desde la primera cita hasta la entrega final, todo organizado.",
  },
];

const features = [
  {
    icon: "ri-user-3-line",
    title: "Client Management",
    desc: "Organiza todos tus clientes en un solo lugar. Historial completo, acceso por portal y seguimiento de leads.",
  },
  {
    icon: "ri-file-text-line",
    title: "Contracts & Payments",
    desc: "Cierra ventas más rápido y cobra sin complicaciones. Contratos digitales, depósitos automáticos y control de balances.",
  },
  {
    icon: "ri-camera-line",
    title: "Job Workflow",
    desc: "Controla cada sesión paso a paso. Agenda de sesiones, progreso en tiempo real y flujo de trabajo estructurado.",
  },
  {
    icon: "ri-image-line",
    title: "Photo Selection",
    desc: "Permite que el cliente elija sus fotos fácilmente. Selección online, favoritos y comentarios.",
  },
  {
    icon: "ri-download-cloud-line",
    title: "Deliveries",
    desc: "Entrega trabajos sin perder archivos. Galerías privadas, descargas seguras y links personalizados.",
  },
  {
    icon: "ri-mail-send-line",
    title: "Digital Invitations",
    desc: "Ofrece invitaciones digitales profesionales. RSVP integrado, página pública y eventos personalizados.",
  },
  {
    icon: "ri-disc-line",
    title: "DVD / Online Media",
    desc: "Entrega final profesional (USB, DVD online, etc). Video embebido, descargas y presentación premium.",
  },
];

const modules = [
  { label: "Clients", icon: "ri-user-3-line", path: "/admin/clients", desc: "Organiza todos tus clientes en un solo lugar. Historial completo, acceso por portal y seguimiento de leads." },
  { label: "Contracts", icon: "ri-file-text-line", path: "/admin/contracts", desc: "Cierra ventas más rápido y cobra sin complicaciones. Contratos digitales, depósitos automáticos y control de balances." },
  { label: "Payments", icon: "ri-money-dollar-circle-line", path: "/admin/payments", desc: "Log and track all payment methods — Zelle, card, cash and more." },
  { label: "Jobs", icon: "ri-camera-line", path: "/admin/jobs", desc: "Controla cada sesión paso a paso. Agenda de sesiones, progreso en tiempo real y flujo de trabajo estructurado." },
  { label: "Invitations", icon: "ri-mail-send-line", path: "/admin/invitations", desc: "Ofrece invitaciones digitales profesionales. RSVP integrado, página pública y eventos personalizados." },
  { label: "Media Packages", icon: "ri-disc-line", path: "/admin/media-packages", desc: "Entrega final profesional (USB, DVD online, etc). Video embebido, descargas y presentación premium." },
];

const testimonials = [
  {
    name: "Carlos M.",
    role: "Fotógrafo de bodas",
    avatar: "CM",
    stars: 5,
    quote: "Ahora tengo todo organizado, antes era un caos.",
  },
  {
    name: "Laura P.",
    role: "Estudio familiar",
    avatar: "LP",
    stars: 5,
    quote: "Mis clientes aman el portal y la selección de fotos.",
  },
  {
    name: "Andrés V.",
    role: "Fotógrafo de eventos",
    avatar: "AV",
    stars: 5,
    quote: "He aumentado mis ventas porque todo es más profesional.",
  },
];

const plans = [
  {
    name: "Free",
    price: "$0",
    period: "/mes",
    desc: "Para fotógrafos que están empezando",
    features: ["1 usuario", "50 clientes", "10 trabajos", "Branding básico"],
    cta: "Comenzar Gratis",
    highlighted: false,
  },
  {
    name: "Pro",
    price: "$29",
    period: "/mes",
    desc: "Para estudios en crecimiento",
    features: ["5 usuarios", "500 clientes", "100 trabajos", "Invitaciones digitales", "Entregas avanzadas"],
    cta: "Upgrade",
    highlighted: true,
  },
  {
    name: "Enterprise",
    price: "$79",
    period: "/mes",
    desc: "Para grandes estudios y agencias",
    features: ["Usuarios ilimitados", "Clientes ilimitados", "Trabajos ilimitados", "Todo desbloqueado"],
    cta: "Contactar",
    highlighted: false,
  },
];

export default function Home() {
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsTransitioning(false);
    }, 400);
  }, [isTransitioning]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % heroSlides.length);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5500);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const handleEarlyAccess = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value.trim();
    setFormStatus("sending");
    try {
      const { error } = await supabase
        .from("marketing_leads")
        .insert({ email });
      if (error && error.code !== "23505") throw error;
      setFormStatus("sent");
      form.reset();
    } catch {
      setFormStatus("idle");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0B0F] text-white font-sans">
      <LandingNav />

      {/* HERO SLIDER */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        {/* Slide backgrounds */}
        {heroSlides.map((slide, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-700"
            style={{ opacity: i === currentSlide ? 1 : 0, zIndex: i === currentSlide ? 1 : 0 }}
          >
            <img
              src={slide.image}
              alt={slide.tag}
              className="w-full h-full object-cover object-top"
              style={{ opacity: isTransitioning ? 0.4 : 1, transition: "opacity 0.4s ease" }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
          </div>
        ))}

        {/* Prev arrow */}
        <button
          onClick={prevSlide}
          className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/8 hover:bg-[#C8A96E]/20 border border-white/10 hover:border-[#C8A96E]/40 text-white/60 hover:text-[#C8A96E] transition-all cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>

        {/* Next arrow */}
        <button
          onClick={nextSlide}
          className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/8 hover:bg-[#C8A96E]/20 border border-white/10 hover:border-[#C8A96E]/40 text-white/60 hover:text-[#C8A96E] transition-all cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>

        {/* Service tag — top left floating */}
        <div className="absolute top-24 left-4 sm:left-10 z-20 hidden sm:block">
          <div
            className="inline-flex items-center gap-2 bg-[#C8A96E]/15 border border-[#C8A96E]/30 rounded-full px-4 py-1.5"
            style={{
              opacity: isTransitioning ? 0 : 1,
              transform: isTransitioning ? "translateY(-6px)" : "translateY(0)",
              transition: "opacity 0.35s ease, transform 0.35s ease",
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#C8A96E]"></span>
            <span className="text-[#C8A96E] text-xs font-semibold tracking-wider uppercase">
              {heroSlides[currentSlide].tag}
            </span>
          </div>
        </div>

        {/* Main content */}
        <div className="relative z-10 text-center w-full max-w-5xl mx-auto px-6 pt-20">
          {/* Platform badge */}
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C8A96E] animate-pulse"></span>
            <span className="text-white/50 text-xs font-semibold tracking-wider">
              SMART BUSINESS CONTROL FOR PHOTOGRAPHERS
            </span>
          </div>

          {/* Slide title + subtitle — animated */}
          <div
            style={{
              opacity: isTransitioning ? 0 : 1,
              transform: isTransitioning ? "translateY(14px)" : "translateY(0)",
              transition: "opacity 0.4s ease, transform 0.4s ease",
            }}
          >
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-black leading-tight mb-4 tracking-tight">
              {heroSlides[currentSlide].title}
              {heroSlides[currentSlide].gold && (
                <>
                  <br />
                  <span className="text-[#C8A96E]">{heroSlides[currentSlide].gold}</span>
                </>
              )}
            </h1>
            <p className="text-white/50 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
              {heroSlides[currentSlide].subtitle}
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/admin"
              className="bg-[#C8A96E] hover:bg-[#b8965a] text-black text-base font-bold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              Ver Demo en Vivo
            </Link>
            <Link
              to="/register"
              className="border border-white/20 hover:border-white/40 text-white text-base px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              Comenzar Ahora
            </Link>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 mt-10 md:mt-16 pt-8 md:pt-12 border-t border-white/10">
            <div className="text-center">
              <p className="text-3xl font-black text-[#C8A96E]">500+</p>
              <p className="text-white/40 text-xs mt-1">Studios Managed</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-black text-[#C8A96E]">12K+</p>
              <p className="text-white/40 text-xs mt-1">Contracts Created</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-black text-[#C8A96E]">$2.4M</p>
              <p className="text-white/40 text-xs mt-1">Payments Tracked</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-black text-[#C8A96E]">99.9%</p>
              <p className="text-white/40 text-xs mt-1">Uptime SLA</p>
            </div>
          </div>
        </div>

        {/* Dot indicators — bottom center */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                i === currentSlide
                  ? "w-8 h-2 bg-[#C8A96E]"
                  : "w-2 h-2 bg-white/25 hover:bg-white/50"
              }`}
            />
          ))}
        </div>

        {/* Slide counter — bottom right */}
        <div className="absolute bottom-10 right-10 z-20 text-white/25 text-xs font-mono tracking-widest">
          {String(currentSlide + 1).padStart(2, "0")} / {String(heroSlides.length).padStart(2, "0")}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-16 md:py-24 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C8A96E] text-xs font-semibold uppercase tracking-widest mb-3">¿Por qué Pixory AI?</p>
            <h2 className="text-4xl md:text-5xl font-black mb-4">Todo lo que tu estudio necesita,<br />nada de lo que no.</h2>
            <p className="text-white/40 text-lg max-w-xl mx-auto">Diseñado específicamente para fotógrafos. No un CRM genérico adaptado a creativos.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f) => (
              <div key={f.title} className="bg-[#141418] rounded-2xl p-6 border border-white/5 hover:border-[#C8A96E]/20 transition-all group">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#C8A96E]/10 text-[#C8A96E] mb-4 group-hover:bg-[#C8A96E]/15 transition-all">
                  <i className={`${f.icon} text-lg`}></i>
                </div>
                <h3 className="text-white font-bold text-base mb-2">{f.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MODULES — Feature Grid with images */}
      <section id="modules" className="py-24 px-6 bg-[#0D0D12]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C8A96E] text-xs font-semibold uppercase tracking-widest mb-3">Módulos de la Plataforma</p>
            <h2 className="text-4xl md:text-5xl font-black mb-4">Cada módulo, <br />construido a la perfección.</h2>
            <p className="text-white/40 text-lg max-w-xl mx-auto">Explora el panel de administración completo y el portal del cliente — demo en vivo lista.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {modules.map((m) => (
              <Link
                key={m.label}
                to={m.path}
                className="bg-[#141418] rounded-2xl p-6 border border-white/5 hover:border-[#C8A96E]/30 transition-all group cursor-pointer block"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C8A96E]/10 text-[#C8A96E] mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${m.icon} text-base`}></i>
                </div>
                <h3 className="text-white font-bold text-sm mb-1.5">{m.label}</h3>
                <p className="text-white/40 text-xs leading-relaxed">{m.desc}</p>
                <div className="mt-4 flex items-center gap-1 text-[#C8A96E] text-xs font-semibold">
                  Open Module
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HERO SCREENSHOT */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden border border-white/10">
            <img
              src="https://readdy.ai/api/search-image?query=dark%20professional%20dashboard%20interface%20photography%20business%20management%20software%20mockup%20screen%20modern%20UI%20elegant%20minimal%20design&width=1200&height=600&seq=ss1&orientation=landscape"
              alt="Pixory AI Dashboard"
              className="w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0F]/80 to-transparent flex items-end p-8">
              <div>
                <p className="text-white/50 text-xs uppercase tracking-widest mb-1">Admin Panel</p>
                <p className="text-white text-xl font-bold">Smart, fast, and beautifully designed.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-24 px-6 bg-[#0D0D12]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C8A96E] text-xs font-semibold uppercase tracking-widest mb-3">Lo que dicen nuestros clientes</p>
            <h2 className="text-4xl md:text-5xl font-black mb-4">Fotógrafos que ya confían en Pixory AI</h2>
            <p className="text-white/40 text-lg max-w-xl mx-auto">Historias reales de estudios que transformaron su flujo de trabajo.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-[#141418] rounded-2xl p-6 border border-white/5">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.stars }).map((_, i) => (
                    <span key={i} className="w-3.5 h-3.5 flex items-center justify-center text-[#C8A96E]">
                      <i className="ri-star-fill text-xs"></i>
                    </span>
                  ))}
                </div>
                <p className="text-white/70 text-sm leading-relaxed mb-5">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#C8A96E]/15 text-[#C8A96E] font-bold text-sm shrink-0">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{t.name}</p>
                    <p className="text-white/30 text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C8A96E] text-xs font-semibold uppercase tracking-widest mb-3">Precios Simples</p>
            <h2 className="text-4xl md:text-5xl font-black mb-4">Planes para cada estudio</h2>
            <p className="text-white/40 text-lg">Empieza gratis, escala cuando estés listo. Sin cargos ocultos.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {plans.map((p) => (
              <div
                key={p.name}
                className={`rounded-2xl p-6 border transition-all ${
                  p.highlighted
                    ? "bg-[#C8A96E]/5 border-[#C8A96E]/40"
                    : "bg-[#141418] border-white/5"
                }`}
              >
                {p.highlighted && (
                  <div className="inline-block bg-[#C8A96E] text-black text-[10px] font-bold px-2.5 py-0.5 rounded-full mb-3 uppercase tracking-wider">
                    Más popular
                  </div>
                )}
                <h3 className="text-white font-bold text-lg">{p.name}</h3>
                <p className="text-white/40 text-xs mb-4">{p.desc}</p>
                <div className="mb-5">
                  <span className="text-4xl font-black text-white">{p.price}</span>
                  <span className="text-white/40 text-sm">{p.period}</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {p.features.map((feat) => (
                    <li key={feat} className="flex items-center gap-2 text-sm text-white/60">
                      <span className="w-4 h-4 flex items-center justify-center text-[#C8A96E] shrink-0">
                        <i className="ri-checkbox-circle-line text-xs"></i>
                      </span>
                      {feat}
                    </li>
                  ))}
                </ul>
                <a
                  href="#early-access"
                  className={`block text-center py-2.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    p.highlighted
                      ? "bg-[#C8A96E] hover:bg-[#b8965a] text-black"
                      : "border border-white/15 hover:border-white/30 text-white"
                  }`}
                >
                  {p.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + EARLY ACCESS FORM */}
      <section id="early-access" className="py-24 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=elegant%20dark%20photography%20studio%20background%20camera%20lens%20bokeh%20golden%20hour%20soft%20professional%20minimal%20abstract&width=800&height=500&seq=cta1&orientation=landscape"
              alt="CTA background"
              className="absolute inset-0 w-full h-full object-cover opacity-15"
            />
            <div className="relative z-10 p-10 text-center">
              <h2 className="text-4xl font-black mb-3">Empieza a organizar tu<br />negocio hoy.</h2>
              <p className="text-white/50 mb-8 text-base">
                Únete a fotógrafos que ya están automatizando su flujo.
              </p>
              {formStatus === "sent" ? (
                <div className="bg-emerald-400/10 border border-emerald-400/30 rounded-2xl p-6">
                  <span className="w-10 h-10 flex items-center justify-center text-emerald-400 mx-auto mb-3">
                    <i className="ri-checkbox-circle-line text-3xl"></i>
                  </span>
                  <p className="text-emerald-400 font-semibold">¡Ya estás dentro!</p>
                  <p className="text-white/40 text-sm mt-1">Te contactaremos en cuanto el acceso esté disponible.</p>
                </div>
              ) : (
                <form
                  onSubmit={handleEarlyAccess}
                  className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                >
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="flex-1 bg-[#141418] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-[#C8A96E]/50 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === "sending"}
                    className="bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-60 text-black font-bold px-6 py-3 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === "sending" ? "Enviando..." : "Obtener acceso"}
                  </button>
                </form>
              )}
              <p className="text-white/20 text-xs mt-4">Sin spam. Sin tarjeta de crédito. Acceso anticipado gratuito.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#141418] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div>
              <p className="text-white font-semibold text-sm mb-4">Platform</p>
              <div className="space-y-2.5">
                <Link to="/admin" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Admin Panel</Link>
                <Link to="/admin/clients" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Clients</Link>
                <Link to="/admin/contracts" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Contracts</Link>
                <Link to="/admin/payments" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Payments</Link>
              </div>
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-4">Product</p>
              <div className="space-y-2.5">
                <a href="#features" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Features</a>
                <a href="#pricing" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Pricing</a>
                <a href="#modules" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Modules</a>
                <a href="#early-access" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Early Access</a>
              </div>
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-4">Support</p>
              <div className="space-y-2.5">
                <Link to="/help-center" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Documentation</Link>
                <Link to="/help-center" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Help Center</Link>
                <a href="mailto:support@pixory.ai" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Contact Us</a>
                <Link to="/changelog" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Changelog</Link>
              </div>
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-4">Legal</p>
              <div className="space-y-2.5">
                <Link to="/privacy-policy" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</Link>
                <Link to="/terms" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Terms of Service</Link>
                <Link to="/cookies" className="block text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Cookie Policy</Link>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between pt-8 border-t border-white/5">
            <div className="flex items-center gap-3 mb-4 sm:mb-0">
              <img
                src="https://static.readdy.ai/image/e50d925263add9c47b947a5709171c8c/6cb7fb8d08f57254bfb69080e4b6eb8a.png"
                alt="Pixory AI"
                className="h-6 object-contain"
              />
              <div>
                <p className="text-white font-bold text-sm tracking-widest">Pixory AI</p>
                <p className="text-white/20 text-[10px]">Smart Business Control for Photographers</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <a href="#" rel="nofollow" onClick={(e) => e.preventDefault()} className="w-8 h-8 flex items-center justify-center text-white/30 hover:text-white transition-colors cursor-pointer">
                <i className="ri-instagram-line"></i>
              </a>
              <a href="#" rel="nofollow" onClick={(e) => e.preventDefault()} className="w-8 h-8 flex items-center justify-center text-white/30 hover:text-white transition-colors cursor-pointer">
                <i className="ri-linkedin-line"></i>
              </a>
              <a href="#" rel="nofollow" onClick={(e) => e.preventDefault()} className="w-8 h-8 flex items-center justify-center text-white/30 hover:text-white transition-colors cursor-pointer">
                <i className="ri-youtube-line"></i>
              </a>
              <span className="text-white/20 text-xs ml-2">© 2025 Pixory AI</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
